import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Search, AlertTriangle, Clock, User, MessageSquare, CheckCircle, XCircle, Eye } from "lucide-react";
import { format } from "date-fns";

interface SupportTicket {
  id: string;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  is_anonymous: boolean;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
  user_id: string;
}

interface TicketResponse {
  id: string;
  message: string;
  is_internal_note: boolean;
  user_id: string;
  created_at: string;
}

interface UserProfile {
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
}

const statusColors = {
  open: "bg-blue-500/10 text-blue-500",
  in_progress: "bg-yellow-500/10 text-yellow-500",
  pending_user: "bg-orange-500/10 text-orange-500",
  resolved: "bg-green-500/10 text-green-500",
  closed: "bg-gray-500/10 text-gray-500"
};

const priorityColors = {
  low: "bg-green-500/10 text-green-500",
  medium: "bg-yellow-500/10 text-yellow-500",
  high: "bg-orange-500/10 text-orange-500",
  urgent: "bg-red-500/10 text-red-500"
};

const categoryLabels = {
  technical: "Technical",
  financial: "Financial",
  behavioral: "Behavioral",
  whistleblower: "Whistleblower",
  account: "Account",
  session: "Session",
  billing: "Billing",
  refund_request: "Refund Request",
  other: "Other"
};

export default function AdminSupportTickets() {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<SupportTicket[]>([]);
  const [userProfiles, setUserProfiles] = useState<Record<string, UserProfile>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [responses, setResponses] = useState<TicketResponse[]>([]);
  const [newResponse, setNewResponse] = useState("");
  const [newInternalNote, setNewInternalNote] = useState("");
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const { toast } = useToast();

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    filterTickets();
  }, [tickets, searchTerm, statusFilter, categoryFilter, priorityFilter, activeTab]);

  const fetchTickets = async () => {
    try {
      const { data: ticketData, error: ticketError } = await supabase
        .from('support_tickets')
        .select('*')
        .order('updated_at', { ascending: false });

      if (ticketError) throw ticketError;

      // Fetch user profiles for all ticket creators
      const userIds = [...new Set(ticketData?.map(t => t.user_id) || [])];
      if (userIds.length > 0) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('user_id, email, first_name, last_name, role')
          .in('user_id', userIds);

        if (profileError) throw profileError;

        const profileMap: Record<string, UserProfile> = {};
        profileData?.forEach(profile => {
          profileMap[profile.user_id] = profile;
        });
        setUserProfiles(profileMap);
      }

      setTickets(ticketData || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast({
        title: "Error loading tickets",
        description: "There was a problem loading support tickets.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    let filtered = tickets;

    // Tab filtering
    if (activeTab === "urgent") {
      filtered = filtered.filter(ticket => 
        ticket.priority === 'urgent' || 
        ticket.category === 'behavioral' || 
        ticket.category === 'whistleblower'
      );
    } else if (activeTab === "open") {
      filtered = filtered.filter(ticket => ticket.status === 'open');
    } else if (activeTab === "assigned") {
      filtered = filtered.filter(ticket => ticket.assigned_to);
    }

    // Search filtering
    if (searchTerm) {
      filtered = filtered.filter(ticket =>
        ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        userProfiles[ticket.user_id]?.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filtering
    if (statusFilter !== "all") {
      filtered = filtered.filter(ticket => ticket.status === statusFilter);
    }

    // Category filtering
    if (categoryFilter !== "all") {
      filtered = filtered.filter(ticket => ticket.category === categoryFilter);
    }

    // Priority filtering
    if (priorityFilter !== "all") {
      filtered = filtered.filter(ticket => ticket.priority === priorityFilter);
    }

    setFilteredTickets(filtered);
  };

  const fetchTicketResponses = async (ticketId: string) => {
    try {
      const { data, error } = await supabase
        .from('ticket_responses')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setResponses(data || []);
    } catch (error) {
      console.error('Error fetching responses:', error);
    }
  };

  const updateTicketStatus = async (ticketId: string, status: string) => {
    try {
      const { error } = await supabase
        .from('support_tickets')
        .update({ 
          status,
          resolved_at: status === 'resolved' ? new Date().toISOString() : null
        })
        .eq('id', ticketId);

      if (error) throw error;

      fetchTickets();
      if (selectedTicket) {
        setSelectedTicket({ ...selectedTicket, status });
      }

      toast({
        title: "Status updated",
        description: `Ticket status changed to ${status}`,
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error updating status",
        description: "There was a problem updating the ticket status.",
        variant: "destructive",
      });
    }
  };

  const assignTicket = async (ticketId: string, adminId: string) => {
    try {
      const { error } = await supabase
        .from('support_tickets')
        .update({ assigned_to: adminId })
        .eq('id', ticketId);

      if (error) throw error;

      fetchTickets();
      toast({
        title: "Ticket assigned",
        description: "Ticket has been assigned successfully.",
      });
    } catch (error) {
      console.error('Error assigning ticket:', error);
      toast({
        title: "Error assigning ticket",
        description: "There was a problem assigning the ticket.",
        variant: "destructive",
      });
    }
  };

  const submitResponse = async (isInternal: boolean) => {
    if (!selectedTicket) return;

    const message = isInternal ? newInternalNote : newResponse;
    if (!message.trim()) return;

    try {
      setIsSubmittingResponse(true);
      const user = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('ticket_responses')
        .insert({
          ticket_id: selectedTicket.id,
          user_id: user.data.user?.id!,
          message,
          is_internal_note: isInternal,
        });

      if (error) throw error;

      if (isInternal) {
        setNewInternalNote("");
      } else {
        setNewResponse("");
        // Update ticket status to pending_user if it was open
        if (selectedTicket.status === 'open') {
          updateTicketStatus(selectedTicket.id, 'in_progress');
        }
      }

      fetchTicketResponses(selectedTicket.id);
      
      toast({
        title: isInternal ? "Internal note added" : "Response sent",
        description: isInternal ? "Internal note has been added." : "Response has been sent to the user.",
      });
    } catch (error) {
      console.error('Error submitting response:', error);
      toast({
        title: "Error submitting response",
        description: "There was a problem submitting the response.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingResponse(false);
    }
  };

  const openTicketDetail = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    fetchTicketResponses(ticket.id);
  };

  const getTicketStats = () => {
    const stats = {
      total: tickets.length,
      open: tickets.filter(t => t.status === 'open').length,
      urgent: tickets.filter(t => t.priority === 'urgent' || t.category === 'behavioral' || t.category === 'whistleblower').length,
      resolved: tickets.filter(t => t.status === 'resolved').length,
    };
    return stats;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  const stats = getTicketStats();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-glow">Support Ticket Management</h1>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="obsidian-card">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-energy">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total Tickets</div>
          </CardContent>
        </Card>
        <Card className="obsidian-card">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-500">{stats.open}</div>
            <div className="text-sm text-muted-foreground">Open</div>
          </CardContent>
        </Card>
        <Card className="obsidian-card">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-red-500">{stats.urgent}</div>
            <div className="text-sm text-muted-foreground">Urgent/Critical</div>
          </CardContent>
        </Card>
        <Card className="obsidian-card">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-500">{stats.resolved}</div>
            <div className="text-sm text-muted-foreground">Resolved</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="obsidian-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
              <Input
                placeholder="Search tickets..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending_user">Pending User</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(categoryLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center justify-center">
              <span className="text-sm text-muted-foreground">
                {filteredTickets.length} ticket{filteredTickets.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all">All Tickets</TabsTrigger>
          <TabsTrigger value="urgent" className="text-red-500">Urgent</TabsTrigger>
          <TabsTrigger value="open">Open</TabsTrigger>
          <TabsTrigger value="assigned">Assigned</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {filteredTickets.length === 0 ? (
            <Card className="obsidian-card">
              <CardContent className="p-8 text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No tickets found</h3>
                <p className="text-muted-foreground">
                  No tickets match your current filters.
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredTickets.map((ticket) => {
              const userProfile = userProfiles[ticket.user_id];
              const isUrgent = ticket.priority === 'urgent' || 
                             ticket.category === 'behavioral' || 
                             ticket.category === 'whistleblower';
              
              return (
                <Card 
                  key={ticket.id} 
                  className={`obsidian-card hover:bg-muted/20 transition-colors cursor-pointer ${
                    isUrgent ? 'border-red-500/50' : ''
                  }`}
                >
                  <CardContent className="p-6" onClick={() => openTicketDetail(ticket)}>
                    <div className="flex flex-col md:flex-row gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-2 mb-2">
                          {isUrgent && <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />}
                          <h3 className="font-semibold text-lg">{ticket.title}</h3>
                          {ticket.is_anonymous && (
                            <Badge variant="outline" className="text-xs">Anonymous</Badge>
                          )}
                        </div>
                        
                        <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                          {ticket.description}
                        </p>

                        <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                          <User className="w-4 h-4" />
                          {userProfile ? (
                            <span>
                              {userProfile.first_name && userProfile.last_name 
                                ? `${userProfile.first_name} ${userProfile.last_name}` 
                                : userProfile.email
                              }
                              <Badge variant="outline" className="ml-2 text-xs">
                                {userProfile.role}
                              </Badge>
                            </span>
                          ) : (
                            <span>Loading user...</span>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <Badge className={statusColors[ticket.status as keyof typeof statusColors]}>
                            {ticket.status.replace('_', ' ').toUpperCase()}
                          </Badge>
                          <Badge className={priorityColors[ticket.priority as keyof typeof priorityColors]}>
                            {ticket.priority.toUpperCase()}
                          </Badge>
                          <Badge variant="outline">
                            {categoryLabels[ticket.category as keyof typeof categoryLabels]}
                          </Badge>
                          {ticket.assigned_to && (
                            <Badge variant="outline" className="bg-blue-500/10">
                              Assigned
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {format(new Date(ticket.created_at), 'MMM dd, HH:mm')}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTicketDetail(ticket);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <DialogTitle className="flex items-center gap-2">
                      {selectedTicket.title}
                      {selectedTicket.is_anonymous && (
                        <Badge variant="outline" className="text-xs">Anonymous</Badge>
                      )}
                    </DialogTitle>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge className={statusColors[selectedTicket.status as keyof typeof statusColors]}>
                        {selectedTicket.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                      <Badge className={priorityColors[selectedTicket.priority as keyof typeof priorityColors]}>
                        {selectedTicket.priority.toUpperCase()}
                      </Badge>
                      <Badge variant="outline">
                        {categoryLabels[selectedTicket.category as keyof typeof categoryLabels]}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Select
                      value={selectedTicket.status}
                      onValueChange={(status) => updateTicketStatus(selectedTicket.id, status)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="pending_user">Pending User</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Description</h4>
                    <p className="text-muted-foreground whitespace-pre-wrap bg-muted/50 p-3 rounded">
                      {selectedTicket.description}
                    </p>
                  </div>

                  {responses.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">Conversation History</h4>
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {responses.map((response) => (
                          <div key={response.id} className={`p-3 rounded-lg ${
                            response.is_internal_note 
                              ? 'bg-yellow-500/10 border border-yellow-500/20' 
                              : 'bg-muted/50'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{response.message}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {response.is_internal_note ? 'Internal Note' : 'Response'} • {' '}
                              {format(new Date(response.created_at), 'MMM dd, yyyy HH:mm')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Tabs defaultValue="response" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="response">User Response</TabsTrigger>
                      <TabsTrigger value="internal">Internal Note</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="response" className="space-y-2">
                      <Textarea
                        placeholder="Type response to user..."
                        value={newResponse}
                        onChange={(e) => setNewResponse(e.target.value)}
                      />
                      <Button 
                        onClick={() => submitResponse(false)}
                        disabled={isSubmittingResponse || !newResponse.trim()}
                        className="energy-button"
                      >
                        Send Response
                      </Button>
                    </TabsContent>
                    
                    <TabsContent value="internal" className="space-y-2">
                      <Textarea
                        placeholder="Internal note (not visible to user)..."
                        value={newInternalNote}
                        onChange={(e) => setNewInternalNote(e.target.value)}
                      />
                      <Button 
                        onClick={() => submitResponse(true)}
                        disabled={isSubmittingResponse || !newInternalNote.trim()}
                        variant="outline"
                      >
                        Add Internal Note
                      </Button>
                    </TabsContent>
                  </Tabs>
                </div>

                <div className="space-y-4">
                  <Card className="obsidian-card">
                    <CardHeader>
                      <CardTitle className="text-sm">Ticket Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium">Created:</span>
                        <br />
                        {format(new Date(selectedTicket.created_at), 'MMM dd, yyyy HH:mm')}
                      </div>
                      <div>
                        <span className="font-medium">Last Updated:</span>
                        <br />
                        {format(new Date(selectedTicket.updated_at), 'MMM dd, yyyy HH:mm')}
                      </div>
                      <div>
                        <span className="font-medium">User:</span>
                        <br />
                        {userProfiles[selectedTicket.user_id] ? (
                          <div>
                            {userProfiles[selectedTicket.user_id].first_name && userProfiles[selectedTicket.user_id].last_name 
                              ? `${userProfiles[selectedTicket.user_id].first_name} ${userProfiles[selectedTicket.user_id].last_name}` 
                              : userProfiles[selectedTicket.user_id].email
                            }
                            <Badge variant="outline" className="ml-2 text-xs">
                              {userProfiles[selectedTicket.user_id].role}
                            </Badge>
                          </div>
                        ) : (
                          'Loading...'
                        )}
                      </div>
                      {selectedTicket.assigned_to && (
                        <div>
                          <span className="font-medium">Assigned to:</span>
                          <br />
                          Admin
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}
                      disabled={selectedTicket.status === 'resolved'}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark Resolved
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => updateTicketStatus(selectedTicket.id, 'closed')}
                      disabled={selectedTicket.status === 'closed'}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Close Ticket
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}